import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';

import { tableHelper } from '@builttocreate/engine-utils';
import TableDataCell from './TableDataCell';
import TableFieldMenuCell from './TableFieldMenuCell';
import TableFieldTextCell from './TableFieldTextCell';
import TableFieldImageCell from './TableFieldImageCell';
import TableFieldSelectionCell from './TableFieldSelectionCell';

import FieldTableColumnTypes from '../../../../constants/FieldTableColumnTypes';
import FieldTableCustomColumnIds from '../../../../constants/FieldTableCustomColumnIds';

import computeStyleChanges from '../../../../lib/computeStyleChanges';

const { getCellOperand } = tableHelper;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${(props) => props.backgroundColor};
`;

const StyledNumberCell = styled(TableDataCell)`
  vertical-align: middle;
  text-align: center;
  background-color: ${(props) => props.backgroundColor};
  border-bottom: ${(props) => props.border};
  padding: 3px;
`;

class TableFieldRow extends Component {

  static propTypes = {

    id: PropTypes.string,
    row: PropTypes.object,
    rowIndex: PropTypes.number,
    columns: PropTypes.array,
    columnWidths: PropTypes.object,
    tableLookup: PropTypes.object,
    focused: PropTypes.string,
    disabled: PropTypes.bool,
    isMobileView: PropTypes.bool,

    //Styles
    primaryColor: PropTypes.string,
    dangerColor: PropTypes.string,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,

    //Action handlers
    onCellChange: PropTypes.func,
    onCellUpload: PropTypes.func,
    onCellBlur: PropTypes.func,
    onCellFocus: PropTypes.func,
    onCellKeyPress: PropTypes.func,
    onRowDelete: PropTypes.func,
    onRowDuplicate: PropTypes.func,
    onRowMoveUp: PropTypes.func,
    onRowMoveDown: PropTypes.func,
    onRowAddBelow: PropTypes.func,
    onLoadCompleted: PropTypes.func
  }

  shouldComponentUpdate(nextProps) {
    return shouldComponentUpdate(nextProps, this.props);
  }

  componentDidMount() {

    const { rowIndex, columns, onLoadCompleted } = this.props;

    /**
     * If this row has no image columns then immediately fire onLoadCompleted
     * because we don't have to wait for any images to load.
     */
    const imageColumns = getTableImageColumns({ columns });
    if (onLoadCompleted && imageColumns.length < 1) onLoadCompleted(rowIndex);

  }

  render() {

    const {
      id,
      row,
      rowIndex,
      tableLookup,
      columns,
      columnWidths,
      focused,
      disabled,
      isMobileView,

      //SDK Styles
      primaryColor,
      dangerColor,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderRadius,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,

      //Action handlers
      onCellChange,
      onCellFocus,
      onCellBlur,
      onCellKeyPress,
      onCellUpload,
      onRowDelete,
      onRowDuplicate,
      onRowMoveUp,
      onRowMoveDown,
      onRowAddBelow,
    } = this.props;

    const styles = {
      backgroundColor,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderRadius,
      borderColor,
      borderWidth,
      padding,
      primaryColor,
      dangerColor
    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const cells = columns.map((column) => {

      const rowId = row._id;
      const cell = row.cells && row.cells[column._id] ? row.cells[column._id] : null;
      const cellKey = `${row._id}_${rowIndex}_${column._id}`;
      const cellOperand = getCellOperand(column.operand, rowIndex + 1);
      const isFocused = focused === `${row._id}_${column._id}`;

      let columnWidth = column.width;
      if (columnWidths && columnWidths[column._id] && !column.width) columnWidth = columnWidths[column._id];

      if (column._id === FieldTableCustomColumnIds.rowNumber) {
        return (
          <StyledNumberCell
            disableHover
            key={cellKey}
            {...styles}
            {...{ border, backgroundColor }}
            width={columnWidth}
          >
            {rowIndex + 1}
          </StyledNumberCell>
        );
      } else if (column._id === FieldTableCustomColumnIds.rowMenu) {
        return (
          <TableFieldMenuCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            rowId={row._id}
            rowIndex={rowIndex}
            {...styles}
            padding={0}
            width={columnWidth}
            onAddBelow={onRowAddBelow}
            onDuplicate={onRowDuplicate}
            onMoveUp={onRowMoveUp}
            onMoveDown={onRowMoveDown}
            onDelete={onRowDelete}
          />
        );
      } else if (column.type === FieldTableColumnTypes.dropdown) {
        return (
          <TableFieldSelectionCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            options={column.options}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
          />
        );
      } else if (column.type === FieldTableColumnTypes.image) {
        return (
          <TableFieldImageCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            {...styles}
            isMobileView={isMobileView}
            width={columnWidth}
            onUpload={onCellUpload}
            onChange={onCellChange}
            onFocus={onCellFocus}
            onBlur={onCellBlur}
            onLoadCompleted={this.handleLoadCompleted}
            maxImageWidth={column.maxImageWidth}
            maxImageHeight={column.maxImageHeight}
          />
        );
      } else {
        return (
          <TableFieldTextCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            displayValue={tableLookup[cellOperand]}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
          />
        );
      }

    });

    return (
      <StyledRow
        key={row._id}
        data-testid={`${id}-${row._id}-${rowIndex}-table-field-row`}
        {...{ border, backgroundColor }}
      >
        {cells}
      </StyledRow>
    );

  }

  handleLoadCompleted = (columnId) => {

    const { onLoadCompleted, columns, rowIndex } = this.props;

    const imageColumns = getTableImageColumns({ columns });

    if (!this.loaded) this.loaded = {};
    this.loaded[columnId] = true;

    /**
     * Only update measurements once all fields have been measured.
     */
    if (onLoadCompleted && Object.keys(this.loaded).length === imageColumns.length) onLoadCompleted(rowIndex);

  }

}

export const getTableImageColumns = createSelector(
  (state) => state.columns,
  (columns) => {

    /**
     * Return all image columns that exist inside the tableColumnOrder
     */
    return columns.filter((column) => column.type === FieldTableColumnTypes.image);

  }
);

const shouldComponentUpdate = (nextProps, props) => {

  const { focused, columns, columnWidths, row, tableLookup, disabled } = props;

  const stylesChanged = computeStyleChanges(nextProps, props);

  /**
   * Row is impacted if either the previously editing cell belonged to this
   * row or the next editing cell belongs to this row.
   */
  const editingCellImpactsRow = (focused && focused.indexOf(row._id) !== -1)
    || (nextProps.focused && nextProps.focused.indexOf(row._id) !== -1);

  /**
   * Only pass table lookup for each row.
   */
  if (
    (focused !== nextProps.focused && editingCellImpactsRow)
    || disabled !== nextProps.disabled
    || !isEqual(columns, nextProps.columns)
    || !isEqual(columnWidths, nextProps.columnWidths)
    || !isEqual(tableLookup, nextProps.tableLookup)
    || !isEqual(row, nextProps.row)
    || stylesChanged
  ) {
    return true;
  } else {
    return false;
  }

};

export { shouldComponentUpdate };

export default TableFieldRow;
